import fabric from './fabric';
import variables from './variables';

const minZoom = variables.zoom.min;
const maxZoom = variables.zoom.max;
const zoomPadding = variables.canvasPadding;
let zoomStep = variables.zoom.step;

export const getVisualCenterInViewport = fabricCanvas =>
  new fabric.Point(
    fabricCanvas.getViewportCenter().x + fabricCanvas.getWidth() / 2,
    fabricCanvas.getViewportCenter().y + fabricCanvas.getHeight() / 2,
  ).divide(fabricCanvas.getZoom());

export const isCanvasBiggerThanViewportAtZoom = (
  fabricCanvas,
  canvasSize,
  zoom,
) => {
  const widthRatio =
    ((canvasSize.width + zoomPadding) * zoom) / fabricCanvas.getWidth();
  const heightRatio =
    ((canvasSize.height + zoomPadding) * zoom) / fabricCanvas.getHeight();
  const sizeRatio = Math.max(widthRatio, heightRatio);
  return sizeRatio > 1;
};

export const isCanvasBiggerThanViewport = (fabricCanvas, canvasSize) =>
  isCanvasBiggerThanViewportAtZoom(
    fabricCanvas,
    canvasSize,
    fabricCanvas.getZoom(),
  );

export const updateZoomStepForZoom = zoom => {
  if (zoom >= 10) {
    zoomStep = variables.zoom.step * 8;
  } else if (zoom >= 4) {
    zoomStep = variables.zoom.step * 4;
  } else {
    zoomStep = variables.zoom.step;
  }
};

export const updateViewport = (
  fabricCanvas,
  canvasSize,
  { center, zoomLevel, containerRect = {}, fitZoom = false },
) => {
  let nextZoom;

  if (fitZoom) {
    const containerWidth = containerRect.width || fabricCanvas.getWidth();
    const containerHeight = containerRect.height || fabricCanvas.getHeight();
    // Compute the biggest zoom we can have to fit all canvas and some padding
    const nextZoomW = (containerWidth - zoomPadding) / canvasSize.width;
    const nextZoomH = (containerHeight - zoomPadding) / canvasSize.height;
    nextZoom = Math.min(nextZoomW, nextZoomH);
    // Don't go above 100%
    nextZoom = Math.min(nextZoom, 1.0);
  } else {
    nextZoom = zoomLevel || fabricCanvas.getZoom();
  }
  const nextCenter = center || {
    x: -fabricCanvas.getCenter().left + (canvasSize.width / 2) * nextZoom,
    y: -fabricCanvas.getCenter().top + (canvasSize.height / 2) * nextZoom,
  };
  fabricCanvas.setZoom(nextZoom);
  fabricCanvas.absolutePan(nextCenter);
  updateZoomStepForZoom(nextZoom);
};

export const zoomByIncrement = (fabricCanvas, canvasSize, type) => {
  const roundingFunc = type === 'in' ? Math.floor : Math.ceil;
  // If current zoom can be rounded on a zoom step and is displayed as such,
  // let's use the rounded value
  // Example: 0.499 is displayed as "50%".
  // Zooming in will do 0.499 + 0.005 = 0.504, Math.floor(0.504 / 0.25) * 0.25 = 0.5
  // Then 0.5 + 0.25 = 0.75
  const roundingOffset = type === 'in' ? 0.005 : -0.005;
  const normalizedZoom =
    roundingFunc((fabricCanvas.getZoom() + roundingOffset) / zoomStep) *
    zoomStep;
  const direction = type === 'in' ? 1 : -1;

  let nextZoom = normalizedZoom + direction * zoomStep;

  nextZoom = Math.max(minZoom, Math.min(nextZoom, maxZoom));
  if (isCanvasBiggerThanViewportAtZoom(fabricCanvas, canvasSize, nextZoom)) {
    // Keep the same viewport center as we change zoom when document will be bigger than viewport
    fabricCanvas.zoomToPoint(
      new fabric.Point(
        fabricCanvas.getCenter().left,
        fabricCanvas.getCenter().top,
      ),
      nextZoom,
    );
  } else {
    // Center the document when it's entirely visible within the viewport
    fabricCanvas.setZoom(nextZoom);
    fabricCanvas.absolutePan({
      x: -fabricCanvas.getCenter().left + (canvasSize.width / 2) * nextZoom,
      y: -fabricCanvas.getCenter().top + (canvasSize.height / 2) * nextZoom,
    });
  }
  updateZoomStepForZoom(nextZoom);
};

export const zoomByEvent = (fabricCanvas, canvasSize, event) => {
  let nextZoom;
  if (event.deltaY && event.offsetY) {
    // It's a mousewheel event and we should base the zoom on the deltaY property
    // Can be triggered either by a physical wheel on a good ol' mouse,
    // or by pinching a trackpad in Google Chrome.
    // Zoom increments faster as it increases.

    nextZoom =
      fabricCanvas.getZoom() - event.deltaY / (100 / fabricCanvas.getZoom());
  } else if (event.deltaScale) {
    // It's a gesture event triggered by pinching in Safari.
    nextZoom =
      fabricCanvas.getZoom() + event.deltaScale * fabricCanvas.getZoom();
  }
  if (nextZoom !== undefined) {
    nextZoom = Math.max(minZoom, Math.min(nextZoom, maxZoom));

    if (nextZoom > 10 && event.deltaY) {
      nextZoom =
        event.deltaScale > 0 ? Math.floor(nextZoom) : Math.ceil(nextZoom);
    } else {
      nextZoom = Math.round(nextZoom * 10000) / 10000;
    }

    fabricCanvas.zoomToPoint(
      new fabric.Point(Math.ceil(event.offsetX), Math.ceil(event.offsetY)),
      nextZoom,
    );

    updateZoomStepForZoom(nextZoom);
  }
};

export const panByEvent = (fabricCanvas, canvasSize, event) => {
  const zoom = fabricCanvas.getZoom();
  const canvas = fabricCanvas;
  const canvasCenterX = canvas.getWidth() / 2;
  const canvasCenterY = canvas.getHeight() / 2;
  const margin = 150;
  const xMax = -canvasCenterX * 2 + margin;
  const yMin = canvasSize.height * zoom - margin;
  const xMin = canvasSize.width * zoom - margin;
  const yMax = -canvasCenterY * 2 + margin;
  const pointer = canvas.getPointer(event, true);

  // Can be either a mousewheel event or a simple mousemove event with space bar down
  const movementX =
    event.type === 'wheel'
      ? -event.deltaX
      : pointer.x - (canvas.previousPointer?.x || 0);
  const movementY =
    event.type === 'wheel'
      ? -event.deltaY
      : pointer.y - (canvas.previousPointer?.y || 0);
  canvas.previousPointer = pointer;
  let x = Math.round(canvas.vptCoords.tl.x * zoom - movementX);
  let y = Math.round(canvas.vptCoords.tl.y * zoom - movementY);
  if (x > xMin) {
    x = xMin;
  }
  if (y > yMin) {
    y = yMin;
  }
  if (x < xMax) {
    x = xMax;
  }
  if (y < yMax) {
    y = yMax;
  }

  canvas.absolutePan({ x, y });
};
